import {
  memo,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import {
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Badge,
  Box,
  TabScrollButton,
  TabProps,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { getHasEditableRole } from "stores/modules/entities.room.members/selectors";
import theme from "theme";
import {
  CHAT_SYSTEM_TABS,
  getDisplayRoomMemberDirectMessageTabs,
} from "stores/modules/app.state/selectors";
import { useVisibleTabs } from "hooks/useVisibleTabs";
import { difference } from "lodash-es";
import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  getClientRect,
  Modifier,
  MouseSensor,
  rectIntersection,
  TouchSensor,
  useDndContext,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clamp } from "modules/clamp";
import { createPortal } from "react-dom";
import { getJoinedMessageOrder } from "stores/modules/entities.room.messageTabOrders/selectors";
import { updateCurrentRoomMessageTabOrder } from "stores/modules/entities.room.messageTabOrders/operations";
import { getJoinedMessageGroups } from "stores/modules/entities.rooms/selectors";
import { shallowEqual } from "react-redux";
import { MessageGroup } from "stores/modules/entities.rooms";

const CustomCollision: CollisionDetection = (args) => {
  const collisions = rectIntersection(args);
  if (collisions.length > 0) return collisions;
  return closestCenter(args);
};

export type MessageTab =
  | {
      type: "group";
      messageContent: MessageGroup;
    }
  | {
      type: "default" | "dm";
      messageContent: {
        key: string;
        name: string;
      };
    };

const ChatBoxHeader = () => {
  const d = useAppDispatch();
  const [t] = useTranslation();
  const tab = useAppSelector((state) =>
    store.getAppState(state, "roomChatTab")
  );
  const newMessageCounts = useAppSelector((state) =>
    store.getAppState(state, "roomChatTabs")
  );
  const directMessageChannels = useAppSelector(
    getDisplayRoomMemberDirectMessageTabs
  );
  const joinedMessageGroups = useAppSelector(getJoinedMessageGroups);

  const messageTabs = useMemo(() => {
    const messageTabsMap = new Map<string, MessageTab>();

    // 初期タブ
    messageTabsMap.set("info", {
      type: "default",
      messageContent: { key: "info", name: "情報" },
    });
    messageTabsMap.set("other", {
      type: "default",
      messageContent: { key: "other", name: "雑談" },
    });

    // DM
    directMessageChannels.forEach((dm) => {
      messageTabsMap.set(dm.key, { type: "dm", messageContent: dm });
    });

    // グループチャット
    joinedMessageGroups.forEach((group) => {
      messageTabsMap.set(group.id, { type: "group", messageContent: group });
    });

    return messageTabsMap;
  }, [directMessageChannels, joinedMessageGroups]);
  const tabsOrder = useAppSelector(getJoinedMessageOrder, shallowEqual);
  const hasEditableRole = useAppSelector(getHasEditableRole);
  const beforeSearchTabs = useRef<string[]>([]);
  const limitLeftTab = useRef<HTMLDivElement | null>(null);
  const limitRightTab = useRef<HTMLDivElement | null>(null);
  const toolBarRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 2,
      },
    })
  );

  const measuring = {
    dragOverlay: {
      measure: (node: HTMLElement) => {
        const wrapper = node.parentElement;

        // 初速が早い場合、collisionの計測が意図しないものになることがあるためOverlayのTransformを無視する
        return getClientRect(wrapper ?? node, {
          ignoreTransform: true,
        });
      },
    },
  };

  const [displayOrder, setDisplayOrder] = useState(tabsOrder);

  useEffect(() => {
    setDisplayOrder(tabsOrder);
  }, [tabsOrder]);

  const {
    hasLeftBadge,
    hasRightBadge,
    registerTab,
    disconnectTab,
    tabAreaRef,
    refreshOvserve,
  } = useVisibleTabs();

  useLayoutEffect(() => {
    refreshOvserve();
  }, [displayOrder, refreshOvserve]);

  useEffect(() => {
    const searchTabs = [...CHAT_SYSTEM_TABS, ...tabsOrder];

    const deleteTabs = difference(beforeSearchTabs.current, searchTabs);
    // 以前のタブの配列にしか存在しないタブを監視対象から削除
    deleteTabs.forEach((deleteTab) => {
      disconnectTab(deleteTab);
    });

    // 新しいタブの配列を設定
    beforeSearchTabs.current = searchTabs;

    if (!searchTabs.includes(tab)) {
      d(
        store.appStateMutate((state) => {
          state.roomChatTab = "main";
          state.roomChatTabs["main"] = 0;
        })
      );
    }
  }, [tab, tabsOrder, d, disconnectTab]);

  const onChangeTab = useCallback(
    (_, value) => {
      if (value) {
        d(
          store.appStateMutate((state) => {
            state.roomChatTab = value;
            state.roomChatTabs[value] = 0;
          })
        );
      } else {
        d(
          store.appStateMutate((state) => {
            state.openRoomMessageChannel = true;
          })
        );
      }
    },
    [d]
  );
  const isPrivateTab = useMemo(() => {
    const isSystemTab = CHAT_SYSTEM_TABS.includes(tab);
    const tabInfo = messageTabs.get(tab);
    const isPrivate =
      (tabInfo?.type === "group" &&
        tabInfo.messageContent.kind === "private") ||
      tabInfo?.type === "dm";
    return !isSystemTab && isPrivate;
  }, [tab, messageTabs]);

  const limitRangeModifire: Modifier = useCallback(
    ({ transform, overlayNodeRect }) => {
      const restrictToHorizontalTransform = {
        ...transform,
        y: 0,
      };

      if (!overlayNodeRect) {
        return restrictToHorizontalTransform;
      }

      const limitLeft = limitLeftTab.current;
      const limitRight = limitRightTab.current;
      const scroller =
        toolBarRef.current?.querySelector<HTMLElement>(".MuiTabs-scroller");
      if (!limitLeft || !limitRight || !scroller) {
        return restrictToHorizontalTransform;
      }
      const appBarRect = scroller.getBoundingClientRect();

      const limitLeftRect = limitLeft.getBoundingClientRect();
      const limitRightRect = limitRight.getBoundingClientRect();

      const harfWidth = overlayNodeRect.width / 3;
      const minX =
        Math.max(limitLeftRect.right, appBarRect.left) -
        overlayNodeRect.left -
        harfWidth;
      const maxX =
        Math.min(limitRightRect.left, appBarRect.right) -
        overlayNodeRect.right +
        harfWidth;

      const x = clamp(transform.x, { min: minX, max: maxX });
      return {
        ...restrictToHorizontalTransform,
        x,
      };
    },
    []
  );

  const onDragStart = useCallback(
    (e: DragStartEvent) => {
      onChangeTab(e, e.active.id.toString());
    },
    [onChangeTab]
  );

  const onReorder = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = tabsOrder.indexOf(active.id.toString());
      const newIndex = tabsOrder.indexOf(over.id.toString());

      const newGroupTabs = arrayMove(tabsOrder, oldIndex, newIndex);

      setDisplayOrder(newGroupTabs);
      d(updateCurrentRoomMessageTabOrder(newGroupTabs));
    },
    [d, tabsOrder]
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onReorder}
      measuring={measuring}
      modifiers={[limitRangeModifire]}
      collisionDetection={CustomCollision}
      autoScroll={{
        acceleration: 2.5,
        interval: 12,
        threshold: {
          x: 0.2,
          y: 0,
        },
        layoutShiftCompensation: false,
      }}
    >
      <DragOverlayTab messageTabs={messageTabs} />
      <StyledAppBar position="static" elevation={0}>
        {/** ツールチップの表示などで座標がおかしくなることがあるのでドラッグ中はpointerEventをブロックする */}
        <DragDndPointerBlocked />
        <Toolbar variant="dense" disableGutters ref={toolBarRef}>
          <SortableContext
            items={tabsOrder}
            strategy={horizontalListSortingStrategy}
          >
            <TabsWrap
              tabAreaRef={tabAreaRef}
              onChangeTab={onChangeTab}
              hasLeftBadge={hasLeftBadge}
              hasRightBadge={hasRightBadge}
            >
              <StyledTab
                ref={limitLeftTab}
                label={
                  <Badge
                    // variant="dot"
                    ref={(el) => registerTab("main", el)}
                    color="secondary"
                    badgeContent={newMessageCounts["main"]}
                    invisible={!newMessageCounts["main"]}
                  >
                    {t("メイン")}
                  </Badge>
                }
                id="main"
                value="main"
              />
              {displayOrder.map((tabKey, index) => {
                const tabInfo = messageTabs.get(tabKey);
                if (!tabInfo) return null;
                if (tabInfo.type === "group") {
                  const group = tabInfo.messageContent;
                  return (
                    <SortableTab
                      key={`${group.id}-${index}`}
                      id={group.id}
                      value={group.id}
                      isPrivateSelected={isPrivateTab && tab === group.id}
                      label={
                        <Badge
                          ref={(el) => registerTab(group.id, el)}
                          variant="dot"
                          color="secondary"
                          invisible={!newMessageCounts[group.id]}
                        >
                          <Box display="flex" alignItems="center">
                            {group.kind === "private" && (
                              <LockIcon
                                sx={{ fontSize: "14px", marginRight: "4px" }}
                              />
                            )}
                            {group.name}
                          </Box>
                        </Badge>
                      }
                    />
                  );
                } else {
                  const otherTab = tabInfo.messageContent;
                  return (
                    <SortableTab
                      key={`${otherTab.key}-${index}`}
                      id={otherTab.key}
                      value={otherTab.key}
                      isPrivateSelected={isPrivateTab && tab === otherTab.key}
                      label={
                        <Badge
                          ref={(el) => registerTab(otherTab.key, el)}
                          variant="dot"
                          color="secondary"
                          invisible={!newMessageCounts[otherTab.key]}
                        >
                          {tabInfo.type === "default"
                            ? t(otherTab.name)
                            : otherTab.name || t("匿名さん")}
                        </Badge>
                      }
                    />
                  );
                }
              })}
              <StyledTab
                ref={limitRightTab}
                label={<AddIcon fontSize="small" />}
                value={null}
                disabled={!hasEditableRole}
                aria-label={t("チャットタブを追加する")}
              />
            </TabsWrap>
          </SortableContext>
        </Toolbar>
      </StyledAppBar>
    </DndContext>
  );
};

type TabsWrapProps = PropsWithChildren<{
  tabAreaRef: MutableRefObject<HTMLDivElement | null>;
  onChangeTab: (_: any, value: any) => void;
  hasLeftBadge: boolean;
  hasRightBadge: boolean;
}>;

const TabsWrap = memo(({ children, ...props }: TabsWrapProps) => {
  const { active } = useDndContext();
  const activeKey = active?.id.toString() || null;

  return (
    <TabsContent {...props} activeKey={activeKey}>
      {children}
    </TabsContent>
  );
});

type TabsContentProps = {
  activeKey: string | null;
} & TabsWrapProps;

const TabsContent = memo(
  ({
    tabAreaRef,
    onChangeTab,
    hasLeftBadge,
    hasRightBadge,
    activeKey,
    children,
  }: TabsContentProps) => {
    const tab = useAppSelector((state) =>
      store.getAppState(state, "roomChatTab")
    );

    return (
      <Tabs
        ref={(el) => (tabAreaRef.current = el)}
        variant="scrollable"
        scrollButtons
        value={tab}
        onChange={onChangeTab}
        textColor="inherit"
        allowScrollButtonsMobile
        ScrollButtonComponent={(props) => {
          return (
            <div style={{ position: "relative", display: "inline-flex" }}>
              <TabScrollButton {...props} />
              {!props.disabled && (
                <ScrollBadge
                  direction={props.direction}
                  variant="dot"
                  color="secondary"
                  invisible={
                    props.direction === "left" ? !hasLeftBadge : !hasRightBadge
                  }
                />
              )}
            </div>
          );
        }}
        TabIndicatorProps={{
          sx: {
            display: activeKey ? "none" : undefined,
          },
        }}
      >
        {children}
      </Tabs>
    );
  }
);

type MessageTabsProps = {
  messageTabs: Map<string, MessageTab>;
};

const DragOverlayTab = ({ messageTabs }: MessageTabsProps) => {
  const [t] = useTranslation();
  const { active } = useDndContext();

  const activeTab = useMemo(() => {
    if (!active) return null;
    return messageTabs.get(active.id.toString()) || null;
  }, [active, messageTabs]);

  return (
    <>
      {createPortal(
        <>
          {activeTab && (
            <DragOverlay zIndex={9999}>
              <StyledTab
                style={{
                  opacity: 1,
                  color: "#fff",
                  background: "rgb(0 0 0 / 0.5)",
                }}
                label={
                  <Box display="flex" alignItems="center">
                    {activeTab.type === "group" &&
                      activeTab.messageContent.kind === "private" && (
                        <LockIcon
                          sx={{ fontSize: "14px", marginRight: "4px" }}
                        />
                      )}
                    {activeTab.messageContent.name || t("匿名さん")}
                  </Box>
                }
              />
            </DragOverlay>
          )}
        </>,
        document.body
      )}
    </>
  );
};

const DragDndPointerBlocked = memo(() => {
  const { active } = useDndContext();

  if (!active) return null;

  return <DndPointerBlocked />;
});

interface SortableTabPros extends TabProps {
  id: string;
  isPrivateSelected: boolean;
}

const SortableTab = ({
  id,
  style: settingStyle,
  isPrivateSelected,
  ...props
}: SortableTabPros) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : undefined,
    ...settingStyle,
  };

  return (
    <StyledTab
      {...props}
      ref={setNodeRef}
      style={style}
      sx={{
        ...props.sx,
        "&.Mui-selected": {
          color: isPrivateSelected ? theme.palette.secondary.main : undefined,
        },
      }}
      {...attributes}
      {...listeners}
    />
  );
};

const StyledAppBar = styled(AppBar)`
  background: #212121;
`;

const StyledTab = styled(Tab)`
  min-width: 48px;

  &.MuiTab-textColorSecondary.Mui-selected {
    color: ${theme.palette.secondary.main};
  }
`;

const ScrollBadge = styled(Badge)<{ direction: string }>`
  position: abusolute;
  top: 12px;
  right: ${({ direction }) => (direction === "right" ? "10px" : "30px")};
  opacity: 0.6;
`;

const DndPointerBlocked = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999999;
  pointer-event: auto;
  background: transparent;
`;

export default memo(ChatBoxHeader);
